import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { Field, makeStyles, mergeClasses, tokens, Toolbar, ToolbarButton } from '@fluentui/react-components'
import {
    TextBold20Regular,
    TextBulletListLtr20Regular,
    TextItalic20Regular,
    TextNumberListLtr20Regular,
    TextUnderline20Regular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
// @ts-expect-error("dompurify exports differ from what ts-lint is expecting and cannot see the default export")
import DOMPurify from 'dompurify'
import { findValue, FormikSubFieldProps, useFormikFieldStyles } from './FormikField'

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li']

const sanitize = (html: string): string => DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR: [] }) as string

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    containerFocused: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    toolbar: {
        padding: '2px 4px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
        borderTopLeftRadius: tokens.borderRadiusMedium,
        borderTopRightRadius: tokens.borderRadiusMedium,
    },
    editor: {
        padding: '6px 10px',
        minHeight: '60px',
        cursor: 'text',
        '& .ProseMirror': {
            outline: 'none',
            minHeight: '48px',
        },
        '& .ProseMirror p': {
            margin: 0,
        },
        '& .ProseMirror p + p': {
            marginTop: tokens.spacingVerticalXS,
        },
        '& .ProseMirror ul, & .ProseMirror ol': {
            margin: '4px 0',
            paddingLeft: '24px',
        },
    },
})

export default function FormikRichTextField<Values>(props: FormikSubFieldProps<unknown, Values>): ReactElement {
    const styles = useStyles()
    const fieldStyles = useFormikFieldStyles()
    const { t: translate } = useTranslation()
    const { values, errors, setFieldValue, setFieldTouched } = useFormikContext<Values>()

    const currentValue = useMemo<string>(() => findValue(values, props.fieldName as string) ?? '', [values, props.fieldName])
    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [errors, props.fieldName])
    const validationState = useMemo<'error' | 'none'>(
        () => (findValue(errors, props.fieldName as string) ? 'error' : 'none'),
        [errors, props.fieldName],
    )

    const editor = useEditor({
        extensions: [StarterKit, Underline],
        content: currentValue,
        onUpdate: ({ editor }) => {
            setFieldValue(props.fieldName as string, sanitize(editor.getHTML()), false)
        },
        onBlur: ({ editor }) => {
            setFieldValue(props.fieldName as string, sanitize(editor.getHTML()), false)
            setFieldTouched(props.fieldName as string, true)
        },
    })

    useEffect(() => {
        if (!editor) {
            return
        }
        if (currentValue === editor.getHTML()) {
            return
        }
        editor.commands.setContent(currentValue || '')
    }, [currentValue, editor])

    if (!editor) {
        return <></>
    }

    return (
        <Field
            label={props.renderLabel ?? (props.labelKey ? translate(props.labelKey, { ...props.labelKeyProps }) : undefined)}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey, { ...props.hintKeyProps }) : undefined)}
            validationMessage={validationMessage}
            validationState={validationState}
            className={mergeClasses(props.className, !props.renderLabel && !props.labelKey && fieldStyles.noLabel)}
        >
            <div className={mergeClasses(styles.container, editor.isFocused && styles.containerFocused)}>
                <Toolbar size="small" className={styles.toolbar}>
                    <ToolbarButton
                        appearance={editor.isActive('bold') ? 'primary' : 'subtle'}
                        icon={<TextBold20Regular />}
                        aria-label={translate('richtext.bold', { defaultValue: 'Bold' })}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    />
                    <ToolbarButton
                        appearance={editor.isActive('italic') ? 'primary' : 'subtle'}
                        icon={<TextItalic20Regular />}
                        aria-label={translate('richtext.italic', { defaultValue: 'Italic' })}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    />
                    <ToolbarButton
                        appearance={editor.isActive('underline') ? 'primary' : 'subtle'}
                        icon={<TextUnderline20Regular />}
                        aria-label={translate('richtext.underline', { defaultValue: 'Underline' })}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                    />
                    <ToolbarButton
                        appearance={editor.isActive('bulletList') ? 'primary' : 'subtle'}
                        icon={<TextBulletListLtr20Regular />}
                        aria-label={translate('richtext.bulletList', { defaultValue: 'Bullet list' })}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    />
                    <ToolbarButton
                        appearance={editor.isActive('orderedList') ? 'primary' : 'subtle'}
                        icon={<TextNumberListLtr20Regular />}
                        aria-label={translate('richtext.orderedList', { defaultValue: 'Numbered list' })}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    />
                </Toolbar>
                <div className={styles.editor} onClick={() => editor.chain().focus().run()} onKeyDown={e => e.stopPropagation()}>
                    <EditorContent editor={editor} />
                </div>
            </div>
        </Field>
    )
}
